import { DollarSign, Cpu, Send, Database, UserRoundCheck } from 'lucide-react';
import { type AnalyticsResponse } from '../../services/api/analytics';

interface Props {
  data: AnalyticsResponse | null;
  loading: boolean;
}

const formatUsd = (n: number): string =>
  n.toLocaleString('sv-SE', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatTokens = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString('sv-SE');
};

export const AnalyticsCostCards = ({ data, loading }: Props) => {
  const totals = data?.cost?.totals;
  const byModel = data?.cost?.byModel ?? [];

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        Kostnader (uppskattning)
      </h3>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            <DollarSign className="h-4 w-4" /> Totalt
          </div>
          <div className="text-3xl font-bold text-slate-900">
            {loading ? '\u2013' : formatUsd(totals?.totalCostUsd ?? 0)}
          </div>
          <div className="text-xs text-slate-500 mt-1">AI + leverans + handoff</div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            <Cpu className="h-4 w-4" /> OpenAI
          </div>
          <div className="text-3xl font-bold text-slate-900">
            {loading ? '\u2013' : formatUsd(totals?.aiCostUsd ?? 0)}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {totals
              ? `${formatTokens(totals.promptTokens + totals.completionTokens)} tokens`
              : '\u2013'}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            <Send className="h-4 w-4" /> Kanal-leverans
          </div>
          <div className="text-3xl font-bold text-slate-900">
            {loading ? '\u2013' : formatUsd(totals?.channelCostUsd ?? 0)}
          </div>
          <div className="text-xs text-slate-500 mt-1">Email</div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            <UserRoundCheck className="h-4 w-4" /> Handoff
          </div>
          <div className="text-3xl font-bold text-slate-900">
            {loading ? '\u2013' : formatUsd(totals?.handoffCostUsd ?? 0)}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {totals ? `${totals.handoffCount.toLocaleString('sv-SE')} överlämningar` : '\u2013'}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            <Database className="h-4 w-4" /> Cache-andel
          </div>
          <div className="text-3xl font-bold text-slate-900">
            {loading || !totals || totals.promptTokens === 0
              ? '\u2013'
              : `${Math.round((totals.cachedPromptTokens / totals.promptTokens) * 100)}%`}
          </div>
          <div className="text-xs text-slate-500 mt-1">Av prompt-tokens</div>
        </div>
      </div>

      {!loading && byModel.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
            Kostnad per modell
          </div>
          <div className="space-y-2">
            {byModel.map((row) => {
              const pct =
                totals && totals.aiCostUsd > 0
                  ? Math.round((row.aiCostUsd / totals.aiCostUsd) * 100)
                  : 0;
              return (
                <div key={row.model} className="flex items-center gap-3">
                  <div className="w-32 flex-shrink-0">
                    <span className="text-xs font-semibold text-slate-700 font-mono">
                      {row.model}
                    </span>
                  </div>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-indigo-500"
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                  <div className="w-40 flex-shrink-0 text-right text-xs">
                    <span className="font-semibold text-slate-800">{formatUsd(row.aiCostUsd)}</span>
                    <span className="text-slate-400">
                      {' '}
                      · {formatTokens(row.promptTokens + row.completionTokens)} tok
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
